import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Area } from "../../types/area";
import { ModalLayout } from "../../components/Modal/ModalLayout";
import { FormField } from "../../components/Form/FormField";
import { useAreas } from "../../hooks/useAreas";

interface EditAreaModalProps {
  isOpen: boolean;
  onClose: () => void;
  area: Area;
}

interface AreaFormValues {
  name: string;
  code: string;
  coordinate: {
    lat: string;
    long: string;
  };
  geometry: string;
}

export const EditAreaModal = ({ isOpen, onClose, area }: EditAreaModalProps) => {
  const { t } = useTranslation();
  const { updateArea } = useAreas();

  const validationSchema = Yup.object({
    name: Yup.string().required(t("validation.required")),
    code: Yup.string().required(t("validation.required")),
    coordinate: Yup.object({
      lat: Yup.number()
        .required(t("validation.required"))
        .typeError(t("validation.number")),
      long: Yup.number()
        .required(t("validation.required"))
        .typeError(t("validation.number")),
    }),
  });

  const formik = useFormik<AreaFormValues>({
    initialValues: {
      name: area.name,
      code: area.code,
      coordinate: {
        lat: area.coordinate.lat.toString(),
        long: area.coordinate.long.toString(),
      },
      geometry: area.geometry || "",
    },
    validationSchema,
    onSubmit: (values) => {
      const updatedArea = {
        name: values.name,
        // code: values.code,
        coordinate: {
          lat: Number(values.coordinate.lat),
          long: Number(values.coordinate.long),
        },
        geometry: values.geometry,
      };

      updateArea.mutate(
        { code: area.code, data: updatedArea },
        {
          onSuccess: () => {
            onClose();
          },
        }
      );
    },
    enableReinitialize: true,
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={handleClose}
      title={t("areas.modal.editArea")}
    >
      <form onSubmit={formik.handleSubmit}>
        <div className="space-y-4">
          <FormField
            label={t("areas.modal.name")}
            name="name"
            placeholder={t("areas.modal.namePlaceholder")}
            formik={formik as unknown as ReturnType<typeof useFormik>}
          />

          <FormField
            label={t("areas.modal.code")}
            name="code"
            placeholder={t("areas.modal.codePlaceholder")}
            formik={formik as unknown as ReturnType<typeof useFormik>}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              label={t("areas.modal.latitude")}
              name="coordinate.lat"
              type="number"
              placeholder={t("areas.modal.latitudePlaceholder")}
              formik={formik as unknown as ReturnType<typeof useFormik>}
            />

            <FormField
              label={t("areas.modal.longitude")}
              name="coordinate.long"
              type="number"
              placeholder={t("areas.modal.longitudePlaceholder")}
              formik={formik as unknown as ReturnType<typeof useFormik>}
            />
          </div>

          <FormField
            label={t("areas.modal.geometry")}
            name="geometry"
            type="textarea"
            placeholder={t("areas.modal.geometryPlaceholder")}
            formik={formik as unknown as ReturnType<typeof useFormik>}
          />
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" className="btn btn-ghost" onClick={handleClose}>
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={formik.isSubmitting}
          >
            {t("areas.modal.save")}
          </button>
        </div>
      </form>
    </ModalLayout>
  );
}; 